import { Q } from '@nozbe/watermelondb';
import { database, txn } from '@/db';
import {
  Invoice,
  LineItem,
  INVOICE_STATUS,
  PAYMENT_STATUS_VOID,
  VOIDED_INVOICE_STATUSES,
} from '@/db/models/Billing';
import { Customer } from '@/db/models/Customers';
import { Batch, Product, StockMovement } from '@/db/models/Inventory';
import { planBatchRestore } from '@/inventory/batchAllocation';
import { outstandingBatchDraw, restoredQtyForSale } from '@/inventory/saleAllocations';
import { logActivity } from '@/utils/auditLog';

export async function voidInvoice(invoiceId: string, userId: string, reason: string): Promise<{ ok: boolean; message?: string }> {
  let voided: { number: string; total: number; storeId: string } | null = null;
  try {
    await txn(async (db) => {
      const invoice = await db.get<Invoice>('invoices').find(invoiceId);
      if (VOIDED_INVOICE_STATUSES.includes(invoice.status)) throw new Error('Invoice is already void.');
      if (invoice.type !== 'SALE') throw new Error('Only sale invoices can be voided.');
      voided = { number: invoice.invoiceNumber, total: invoice.totalAmount, storeId: invoice.storeId };

      const lines = await db.get<LineItem>('line_items').query(
        Q.where('invoice_id', invoiceId),
        Q.where('is_deleted', false),
      ).fetch();

      // Aggregate by product: the same product can appear on several lines, and
      // "already returned" is a per-product fact, not a per-line one.
      const soldByProduct = new Map<string, number>();
      for (const line of lines) {
        soldByProduct.set(line.productId, (soldByProduct.get(line.productId) ?? 0) + line.quantity);
      }

      for (const [productId, soldQty] of soldByProduct) {
        const product = await db.get<Product>('products').find(productId).catch(() => null);
        if (!product || product.isService || !product.trackInventory) continue;

        // Voiding an invoice that already carries a partial credit note must only
        // reverse what the sale STILL holds. Restoring the full line quantity would
        // put back more units than ever left the shelf.
        const alreadyRestored = await restoredQtyForSale(invoice.id, productId);
        const outstanding = Math.max(0, soldQty - alreadyRestored);
        if (outstanding === 0) continue;

        await product.update((record) => {
          record.currentStock = product.currentStock + outstanding;
          record.version = product.version + 1;
          record.isSynced = false;
        });

        // Put the units back into the batches the sale drew from. `outstandingBatchDraw`
        // nets out earlier returns too, so batch and product stay in step. Untagged
        // units (non-batch product, or a sale predating batch tagging) go back
        // product-level only.
        const draw = await outstandingBatchDraw(invoice.id, productId);
        const plan = planBatchRestore(draw, outstanding);

        const emit = async (batchId: string | null, quantity: number) => {
          await db.get<StockMovement>('stock_movements').create((record) => {
            record.productId = productId;
            record.godownId = null;
            record.batchId = batchId;
            record.type = 'VOID_REVERSAL';
            record.quantity = quantity;
            record.referenceId = invoice.id;
            record.referenceType = 'INVOICE_VOID';
            record.notes = reason.trim() || `Void ${invoice.invoiceNumber}`;
            record.source = 'DESKTOP';
            record.userId = userId;
            record.version = 1;
            record.isSynced = false;
            record.isDeleted = false;
          });
        };

        for (const allocation of plan.allocations) {
          const batchRecord = await db.get<Batch>('batches')
            .find(allocation.batchId).catch(() => null);
          if (batchRecord) {
            await batchRecord.update((record) => {
              record.quantity = batchRecord.quantity + allocation.allocatedQty;
              record.version = batchRecord.version + 1;
              record.isSynced = false;
            });
          }
          await emit(allocation.batchId, allocation.allocatedQty);
        }

        if (plan.untagged > 0) await emit(null, plan.untagged);
      }

      if (invoice.customerId && invoice.dueAmount > 0) {
        const customer = await db.get<Customer>('customers').find(invoice.customerId).catch(() => null);
        if (customer) {
          await customer.update((record) => {
            record.balance = Math.max(0, customer.balance - invoice.dueAmount);
            record.version = customer.version + 1;
            record.isSynced = false;
          });
        }
      }

      await invoice.update((record) => {
        record.status = INVOICE_STATUS.VOIDED;
        record.paymentStatus = PAYMENT_STATUS_VOID;
        record.notes = [invoice.notes, `VOID: ${reason.trim() || 'No reason provided'}`].filter(Boolean).join('\n');
        record.version = invoice.version + 1;
        record.isSynced = false;
      });
    });
    if (voided) {
      const v = voided as { number: string; total: number; storeId: string };
      void logActivity({
        action: 'invoice_voided',
        entityType: 'invoice',
        entityId: invoiceId,
        storeId: v.storeId,
        userId,
        changes: {
          summary: `${v.number} voided · ${reason.trim() || 'no reason'}`,
          amount: -v.total,
          ref: v.number,
        },
      });
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : 'Failed to void invoice.' };
  }
}
